import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { AlertTriangle, Printer, Loader2 } from "lucide-react";
import { releaseImpoundToNts, quoteImpound } from "../lib/api";
import { toast } from "sonner";
import PrintReceipt from "./PrintReceipt";
import PrintReleaseFormButton from "./PrintReleaseFormButton";
import { salvageFull, salvageShort, tenantLocale } from "../lib/company";
import { overstayDays } from "../lib/tenantTime";

const fmtMoney = (n) =>
  n == null ? "—" : `$${Number(n).toLocaleString(tenantLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Driver abandoned the vehicle → ownership transfers to the tenant's own
 * salvage entity. Snapshots the amount that *was* owed (for write-off
 * reporting) and prints a single A4 internal "released to" record.
 */
export default function ReleaseToNtsDialog({ job, open, onOpenChange, onReleased }) {
  const [quote, setQuote] = useState(null);
  const [notes, setNotes] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [receipt, setReceipt] = useState(null);

  useEffect(() => {
    if (!open || !job) return;
    setConfirming(false);
    setSaving(false);
    setReceipt(null);
    setNotes("");
    let alive = true;
    (async () => {
      try {
        const overstay = overstayDays(job.impound_release_due_at);
        const q = await quoteImpound({
          gvm_kg: Number(job.gvm_kg || 0),
          impounded_at: job.impounded_at,
          term: job.impound_term || "28d",
          base_to_base_km: job.route_distance_m ? job.route_distance_m / 1000 : null,
          days_overstay: overstay,
          is_public_holiday: job.is_public_holiday || null,
        });
        if (alive) setQuote(q);
      } catch { /* fall back to job.pricing_breakdown */ }
    })();
    return () => { alive = false; };
  }, [open, job]);

  const breakdown = quote || job?.pricing_breakdown;
  const km = job?.route_distance_m ? job.route_distance_m / 1000 : null;

  const onConfirm = async () => {
    setSaving(true);
    try {
      const updated = await releaseImpoundToNts(job.id, {
        release_notes: notes,
        base_to_base_km: km,
      });
      setReceipt(updated);
      toast.success(`Vehicle ownership transferred to ${salvageShort()}`);
      setTimeout(() => window.print(), 250);
      onReleased?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Release-to-${salvageShort()} failed`);
    } finally {
      setSaving(false);
    }
  };

  if (!job) return null;

  return (
    <>
      <Dialog open={open && !receipt} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" data-testid="release-nts-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black tracking-tight text-amber-700 dark:text-amber-300 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Release to {salvageFull()}
            </DialogTitle>
            <DialogDescription>
              {job.rego || "—"} · {job.vehicle} · {job.customer_name}
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-md border-2 border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3 my-2">
            <div className="text-xs font-bold text-amber-900 dark:text-amber-200 mb-1">Driver did not collect the vehicle</div>
            <div className="text-xs text-amber-800 dark:text-amber-300">Ownership will transfer to {salvageFull()} in lieu of payment. The amount owed below is recorded as a write-off for reporting. This cannot be reversed without an admin edit.</div>
          </div>

          {breakdown && (
            <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3" data-testid="release-nts-amount">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-1">Amount Owed (Write-Off)</div>
              <div className="font-mono font-black text-2xl text-amber-900 dark:text-amber-200">{fmtMoney(breakdown.total)}</div>
              <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">Tow {fmtMoney(breakdown.line_items.tow_fee)} · Impound {fmtMoney(breakdown.line_items.impound_fee)} · Km {fmtMoney(breakdown.line_items.additional_km_fee)} · Overstay {fmtMoney(breakdown.line_items.overstay_fee)}</div>
            </div>
          )}

          <div className="mt-3">
            <Label className="uppercase-label">Notes (optional)</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="e.g. multiple contact attempts, registered mail returned, etc." data-testid="release-nts-notes" />
          </div>

          {confirming && (
            <div className="mt-3 rounded-md border-2 border-rose-300 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30 p-3 flex items-start gap-2" data-testid="release-nts-confirm-banner">
              <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0 mt-0.5" />
              <div className="text-sm text-rose-900 dark:text-rose-200">
                <div className="font-bold">Confirm — vehicle to {salvageShort()} ownership?</div>
                <div className="text-xs mt-0.5">This action transfers ownership to {salvageFull()} and prints a write-off record for <strong>{fmtMoney(breakdown?.total)}</strong>. Cannot be undone here.</div>
              </div>
            </div>
          )}

          <DialogFooter className="mt-2 flex-wrap gap-2">
            {!confirming ? (
              <>
                <PrintReleaseFormButton
                  jobId={job?.id}
                  action="signover_to_nts"
                  label="Print Release Form"
                  testId="release-nts-print-form"
                  className="mr-auto"
                />
                <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="release-nts-cancel">Cancel</Button>
                <Button onClick={() => setConfirming(true)} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="release-nts-step1">
                  Transfer to {salvageShort()}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setConfirming(false)} data-testid="release-nts-back">Back</Button>
                <Button onClick={onConfirm} disabled={saving} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="release-nts-confirm-final">
                  {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin"/> : <Printer className="h-3.5 w-3.5 mr-1.5" />}
                  Confirm &amp; Print
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {receipt && (
        <Dialog open={open} onOpenChange={(o) => { if (!o) { setReceipt(null); onOpenChange(false); }}}>
          <DialogContent className="max-w-md" data-testid="release-nts-done-dialog">
            <DialogHeader>
              <DialogTitle className="text-amber-700 dark:text-amber-300">✓ Transferred to {salvageShort()}</DialogTitle>
              <DialogDescription>If the print dialog didn&apos;t appear, click below to reopen it.</DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => window.print()} data-testid="release-nts-reprint"><Printer className="h-3.5 w-3.5 mr-1.5"/>Re-print</Button>
              <Button onClick={() => { setReceipt(null); onOpenChange(false); }} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="release-nts-done">Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {receipt && <PrintReceipt job={receipt} mode="nts" />}
    </>
  );
}
